import { useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import useSeatmapBootstrap from '../../../../hooks/useSeatmapBootstrap'
import useFiltersStore from '../../../../stores/filtersStore'
import { useSeatmap } from '../providers/SeatmapProvider'
import { useModals } from '../providers/ModalsProvider/ModalsProvider'
import {
  formatDateTime,
  formatEventSchedule,
  getTimezoneDate,
  isEventTimeTbd,
  timezones,
  capitalizeString,
} from '../../../../utilities/helpers'
import authService from '../../../../utilities/services/auth.service'
import {
  resolveBrandLogo,
  resolvePrimaryAttractionImage,
  resolvePrimaryColor,
  resolveButtonColor,
  resolveButtonTextColor,
} from '../../utils/branding'

import { TicketSelectionPanel } from './TicketSelectionPanel'
import PurchaseSidebar from './PurchaseSidebar/PurchaseSidebar'

import '../../purchaseFlow.scss'
import './newSeatedPurchaseContainer.scss'
import './seatedPurchasePage.scss'

export default function NewSeatedPurchaseContainer() {
  const event = useFiltersStore((state) => state.event)
  const { hasMap, background } = useSeatmapBootstrap()
  const { showSeatmap } = useSeatmap()
  const { openCBModal } = useModals()
  const navigate = useNavigate()
  const loggedIn = authService.isLoggedIn()

  useEffect(() => {
    if (window.Intercom) {
      window.Intercom('update', { hide_default_launcher: true })
    }
  }, [])

  const organization = event?.organization
  const accent = resolvePrimaryColor(event, organization)
  const buttonColor = resolveButtonColor(event, organization, accent)
  const buttonTextColor = resolveButtonTextColor(event, organization)
  const attractionImage = resolvePrimaryAttractionImage(event)
  const logoSrc = attractionImage?.url || resolveBrandLogo(event, organization)
  const logoAlt =
    attractionImage?.name || organization?.name || event?.name || 'Event'
  const timezone = timezones(event?.venue?.timezone)
  const when = formatEventSchedule(event, timezone)
  const doorsTime =
    !isEventTimeTbd(event) && event?.doorsOpen
      ? formatDateTime(getTimezoneDate(event.doorsOpen, timezone), 'timeOnly')
      : null
  const venueLine = [
    event?.venue?.name,
    event?.venue?.address?.[0]
      ? `${capitalizeString(event.venue.address[0].city)}, ${capitalizeString(
          event.venue.address[0].state
        )}`
      : null,
  ]
    .filter(Boolean)
    .join(', ')

  const providerLabel = organization?.name
    ? `Official ticketing marketplace for ${organization.name} venues`
    : 'Official ticketing marketplace'

  const mapPreview =
    (typeof background === 'string' ? background : background?.url) || null

  return (
    <div
      className="seated-purchase-page spp-listings-first"
      style={{
        '--spp-accent': accent,
        '--spp-button': buttonColor,
        '--spp-button-text': buttonTextColor,
      }}
    >
      <header className="spp-brand-header">
        <div className="spp-brand-header__inner">
          <div className="spp-brand-header__event">
            {logoSrc ? (
              <div
                className={`spp-brand-header__logo-tile${
                  attractionImage ? ' spp-brand-header__logo-tile--photo' : ''
                }`}
              >
                <img
                  className="spp-brand-header__logo"
                  src={logoSrc}
                  alt={logoAlt}
                />
              </div>
            ) : null}
            <div className="spp-brand-header__copy">
              <h1 className="spp-brand-header__title">{event?.name}</h1>
              {(when || venueLine) && (
                <p className="spp-brand-header__meta">
                  {when}
                  {doorsTime ? ` · Doors ${doorsTime}` : ''}
                  {venueLine ? (
                    <>
                      {' '}
                      at{' '}
                      <span className="spp-brand-header__venue">
                        {venueLine}
                      </span>
                    </>
                  ) : null}
                </p>
              )}
              {openCBModal ? (
                <button
                  type="button"
                  className="spp-brand-header__info"
                  onClick={() => openCBModal('Information', event)}
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden
                  >
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="16" x2="12" y2="12" />
                    <line x1="12" y1="8" x2="12.01" y2="8" />
                  </svg>
                  Event information
                </button>
              ) : null}
            </div>
          </div>
          <div className="spp-brand-header__actions">
            <form
              className="spp-search"
              onSubmit={(e) => {
                e.preventDefault()
                const q = new FormData(e.currentTarget).get('q')
                if (q)
                  navigate(`/search?query=${encodeURIComponent(String(q))}`)
              }}
            >
              <input
                name="q"
                placeholder="Search events, artists, or venues"
                aria-label="Search events, artists, or venues"
              />
              <button type="submit" aria-label="Search">
                ⌕
              </button>
            </form>
            {loggedIn ? (
              <Link to="/my-events" className="spp-login-btn">
                My wallet
              </Link>
            ) : (
              <Link to="/login" className="spp-login-btn">
                Login
              </Link>
            )}
          </div>
        </div>
      </header>

      <main className="spp-main">
        <TicketSelectionPanel />
        <PurchaseSidebar
          hasMap={hasMap}
          mapPreview={mapPreview}
          onFindOnMap={showSeatmap}
        />
      </main>

      <div className="spp-market-footer" role="contentinfo">
        <div className="spp-market-footer__inner">
          <span className="spp-market-footer__brand">blocktickets</span>
          <span className="spp-market-footer__divider" aria-hidden />
          <span className="spp-market-footer__label">{providerLabel}</span>
        </div>
      </div>
    </div>
  )
}
